/**
 * O funil de oportunidades — o kanban (spec 10, fatia mínima), na forma do Quadro da equipe do Stonen (01/11).
 *
 * Uma coluna por etapa ABERTA; ganho e perdido não são colunas, são saídas — ficam num resumo no topo
 * e nos cards fechados dos últimos 30 dias, atrás de um interruptor. A regra que a spec 10 §2 mais
 * defende está no card: **"parado há N dias"**, contado do último movimento REAL de etapa, porque um
 * lead que ninguém toca é o que o funil existe para mostrar.
 *
 * Nada aqui decide quem vê o quê: o servidor recorta (`read_team_funnel`) e esconde o valor
 * (`read_funnel_values`); a tela só deixa de desenhar a coluna de valor quando ele não veio.
 */

import { useState, type MouseEvent } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  Botao, EstadoCarregando, EstadoDaFalha, Selo as SeloDoHugi, TextoDaMensagem, falhaDe, identificadorLegivel, useSessao,
  type FalhaDaApi,
} from "@hugi/ui";
import {
  CabecalhoDaTela, Carregando, Gaveta, Kanban, ListView, PaginaInteira, Segmentado, Selo, Vazio, useEstreito, useLembrado, useNaSessao,
  type ColunaDaLista, type ColunaDoKanban, type Tom, type VistaDaLista,
} from "@hugi/ui/kit";
import { CheckSquare, Clock, Hourglass, List, MessageSquareText, Search, Settings2, SlidersHorizontal, SquareKanban, Upload, X } from "lucide-react";
import "./funil.css";
import {
  adicionarItemNoCard, arquivarCampo, arquivarEtapa, arquivarItemDoCatalogo, arquivarMotivoDePerda, arquivarQuadro, cancelarTarefa, concluirTarefa, conferirOportunidades, criarCampo, criarEtapa, criarItemDoCatalogo, criarMotivoDePerda, criarOportunidade, criarQuadro, criarTarefa, editarEtapa, editarQuadro, importarOportunidades, editarOportunidade, ganharOportunidade, moverOportunidade, passarParaAFila, perderOportunidade, prefetchConversa, reabrirOportunidade, removerItemDoCard, responderPelaOportunidade,
  useCampanhas, useCatalogoDoFunil, useContatos, useConversaDaOportunidade, useFunil, useItensDaOportunidade, useTarefasDaOportunidade, useTemplates,
  type CampoDoFunil, type ConferenciaDeOportunidades, type ConversaDoCard, type EtapaDoFunil, type MensagemDaConversaDoCard, type Oportunidade, type QuadroDoFunil, type Template,
} from "./dados.js";

const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ORIGEM: Readonly<Record<Oportunidade["origem"], string>> = {
  campanha: "Campanha",
  conversa: "Conversa",
  anuncio: "Anúncio",
  manual: "Manual",
};

const TEMPERATURA: Readonly<Record<NonNullable<Oportunidade["temperatura"]>, string>> = {
  quente: "🔥 Quente",
  morno: "Morno",
  frio: "❄ Frio",
};

/** Com quem o contato está — a frase e o tom do selo. */
export function comQuemEsta(c: ConversaDoCard | null): { rotulo: string; tom: "ok" | "degradado" | "ruim" | "neutro" } {
  if (!c) return { rotulo: "Sem conversa", tom: "neutro" };
  switch (c.comQuem) {
    case "fila": return { rotulo: "Na fila", tom: "degradado" };
    case "humano": return { rotulo: c.donoNome ? `Com ${c.donoNome}` : "Com um atendente", tom: "ok" };
    case "ia": return { rotulo: "Com a IA", tom: "ok" };
    case "integracao": return { rotulo: "Com a integração", tom: "ok" };
    case "finalizada": return { rotulo: "Conversa fechada", tom: "neutro" };
  }
}

function mensagemDa(f: FalhaDaApi | undefined): string | undefined {
  if (!f) return undefined;
  return "erro" in f && f.erro && typeof f.erro === "object" && "mensagem" in f.erro ? String((f.erro as { mensagem?: unknown }).mensagem ?? "") || undefined : undefined;
}

/** "parado há N dias" — o número que o kanban existe para mostrar. */
/** A idade do card, para a etiqueta: "hoje", "1 d", "9 d", "3 sem", "2 m". */
export function fraseDeIdade(criadaEm: string, agora = new Date()): string {
  const dias = Math.max(0, Math.floor((agora.getTime() - new Date(criadaEm).getTime()) / 86_400_000));
  if (dias === 0) return "hoje";
  if (dias < 14) return `${dias} d`;
  if (dias < 60) return `${Math.floor(dias / 7)} sem`;
  return `${Math.floor(dias / 30)} m`;
}

/** A etiqueta da tarefa do card: vencida em vermelho, hoje em âmbar, o resto neutro. */
export function fraseDaTarefa(t: { readonly quando: string; readonly vencida: boolean }, agora = new Date()): { texto: string; tom: "ruim" | "atencao" | "neutro" } {
  if (t.vencida) return { texto: "Tarefa vencida", tom: "ruim" };
  const q = new Date(t.quando);
  const mesmoDia = q.toDateString() === agora.toDateString();
  if (mesmoDia) return { texto: `Tarefa hoje ${q.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`, tom: "atencao" };
  const dias = Math.ceil((q.getTime() - agora.getTime()) / 86_400_000);
  return { texto: dias === 1 ? "Tarefa amanhã" : `Tarefa em ${dias} d`, tom: "neutro" };
}

export function fraseDeParado(dias: number): string {
  if (dias <= 0) return "hoje";
  if (dias === 1) return "parado há 1 dia";
  return `parado há ${dias} dias`;
}

/*
  O FUNIL É O "QUADRO DA EQUIPE" DO STONEN (owner, 01/11: "Não sei se gosto do funil. Tenta ver como ficou a tela de
  acompanhamento do time, no Stonen" — e, corrigindo, o Quadro da equipe: `stonen/apps/web/src/features/v2/quadro/`).
  Família Mesa de trabalho (spec 22):

      [ícone] Funil de oportunidades (i)  24 abertas · previsão R$ …   [busca] [filtros na mesma régua] [Quadro|Lista]
      KANBAN de cartões densos, uma coluna por etapa aberta + Fechadas; cada coluna rola por dentro
      (o REGISTRO DO CARTÃO abre numa Gaveta ao lado — só quando há cartão escolhido)

  - O CABEÇALHO é o `CabecalhoDaTela`: o recorte em palavras (quantas abertas, a previsão, a campanha que recorta) e, nas
    ferramentas, os FILTROS NUMA RÉGUA SÓ — busca, quadro, campanha, de quem é, os recortes que a coluna não diz
    (paradas há +7 dias, tarefa vencida), a ordem — e a forma de ver, Quadro | Lista (lembrada por tela, `useLembrado`).
    Até 900 px os filtros além da busca vão para a Gaveta "Filtros", junto com Etapas e Importar.
  - ⚠️ REVERSÃO DE DECISÃO (01/11): "a coluna da conversa sempre visível" era pedido do owner (spec 23, J4 mantido). Ele
    pediu explicitamente o padrão do Stonen, e lá o registro do cartão — com a conversa dele — só abre quando há cartão
    escolhido. Por isso a conversa saiu da coluna fixa e foi para o REGISTRO (Gaveta): o quadro usa a largura toda.
  - O REGISTRO DO CARTÃO: no topo os selos (com quem, parado há, etapa, valor, responsável, campanha), as abas Conversa
    | Detalhes (tarefas, campos, itens, valor, passar para a fila), e no pé, FIXA, a barra de ações: mover, perder (com o
    motivo) e Ganhar — a única primária. Clicar no cartão abre o registro (antes abria a conversa, e o detalhe ficava
    atrás de um ícone).
  - A LISTA só lê: uma linha por cartão (contato, etapa, origem, valor, parado há, responsável, próxima tarefa); a linha
    abre o registro.
  - Celular: uma coluna por vez (seletor segmentado), e o registro ocupa a tela.
  - `?campanha=`, `?quadro=` e `?card=` continuam na URL.
*/
type Recorte = "parados" | "vencida";
type Ordem = "parado" | "valor" | "recente" | "nome";
const DIAS_PARADO = 7;
const MODOS = ["quadro", "lista"] as const;
type Modo = (typeof MODOS)[number];

const nomeDo = (c: Oportunidade) => c.contatoNome ?? identificadorLegivel(c.identificador);

export function Funil() {
  const sessao = useSessao();
  const estreito = useEstreito();
  const [novo, setNovo] = useState(false);
  const [editandoEtapas, setEditandoEtapas] = useState(false);
  const [importando, setImportando] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [modoLembrado, setModo] = useLembrado<Modo>("hugi:funil:modo", "quadro", (v) => (MODOS as readonly string[]).includes(v));
  // `?modo=lista|quadro` abre naquela forma (link e trava); sem ele, vale a escolha lembrada.
  const [modoDaUrl, setModoDaUrl] = useState<Modo | null>(() => {
    const m = new URLSearchParams(window.location.search).get("modo");
    return m === "lista" || m === "quadro" ? m : null;
  });
  const modo = modoDaUrl ?? modoLembrado;
  const [busca, setBusca] = useNaSessao("hugi-funil:busca", "");
  const [deQuem, setDeQuem] = useNaSessao<string>("hugi-funil:de-quem", "todos");
  const [recorte, setRecorte] = useNaSessao<Recorte | null>("hugi-funil:recorte", null);
  const [ordem, setOrdem] = useNaSessao<Ordem>("hugi-funil:ordem", "parado");
  const [colunaNoCelular, setColunaNoCelular] = useState<string | null>(null);
  const podeImportar = sessao.permissoes.includes("manage_contact");
  // A campanha filtrada também é a URL (`?campanha=<id>`, spec 23, jornada 5): é por ela que "Leads" no detalhe da
  // campanha abre o funil já recortado.
  const [campanhaId, setCampanhaEstado] = useState<string | null>(() => new URLSearchParams(window.location.search).get("campanha"));
  const setCampanhaId = (id: string | null) => {
    setCampanhaEstado(id);
    const url = new URL(window.location.href);
    if (id) url.searchParams.set("campanha", id); else url.searchParams.delete("campanha");
    window.history.replaceState(null, "", url);
  };
  // O quadro aberto é a URL (`?quadro=`), como o card: mandar o link do quadro de Pós-venda funciona.
  const [quadroId, setQuadroEstado] = useState<string | null>(() => new URLSearchParams(window.location.search).get("quadro"));
  const setQuadroId = (id: string | null) => {
    setQuadroEstado(id);
    const url = new URL(window.location.href);
    if (id) url.searchParams.set("quadro", id); else url.searchParams.delete("quadro");
    window.history.replaceState(null, "", url);
  };
  // O cartão aberto é a URL (`?card=<id>`): dá para mandar "olha este lead" para um colega, e recarregar não fecha.
  const [aberto, setAbertoEstado] = useState<string | null>(() => new URLSearchParams(window.location.search).get("card"));
  const setAberto = (id: string | null) => {
    setAbertoEstado(id);
    const url = new URL(window.location.href);
    if (id) url.searchParams.set("card", id); else url.searchParams.delete("card");
    window.history.replaceState(null, "", url);
  };
  const quadro = useFunil(30, campanhaId, quadroId);
  const campanhas = useCampanhas();
  const podeGerirCatalogo = sessao.permissoes.includes("manage_catalog");
  const podeTransferir = sessao.permissoes.includes("transfer_conversation");

  const titulo = "Funil de oportunidades";
  const icone = <SquareKanban size={18} />;
  if (quadro.isLoading) return <PaginaInteira rotulo={titulo}><CabecalhoDaTela icone={icone} titulo={titulo} /><Carregando /></PaginaInteira>;
  if (quadro.isError) return <PaginaInteira rotulo={titulo}><CabecalhoDaTela icone={icone} titulo={titulo} /><EstadoDaFalha falha={falhaDe(quadro.error)!} aoRepetir={() => void quadro.refetch()} /></PaginaInteira>;
  const q = quadro.data!;

  const abertas = q.etapas.filter((e) => e.tipo === "aberta");
  const cardsAbertos = q.cards.filter((c) => c.status === "aberta");
  const ganhos = q.cards.filter((c) => c.status === "ganha");
  const perdidos = q.cards.filter((c) => c.status === "perdida");
  const valorGanho = ganhos.reduce((s, c) => s + (c.valorCentavos ?? 0), 0);
  const registro = aberto ? (q.cards.find((c) => c.id === aberto) ?? null) : null;

  // Os responsáveis que aparecem nos cartões — o "de quem é" recorta pelo id; "Meus" é o da sessão.
  const responsaveis = [...new Map(q.cards.filter((c) => c.responsavelId).map((c) => [c.responsavelId!, c.responsavelNome ?? "Sem nome"])).entries()];
  const procurado = busca.trim().toLocaleLowerCase("pt-BR");
  const passa = (c: Oportunidade) =>
    (deQuem === "todos" || (deQuem === "meus" ? c.responsavelId === sessao.usuario.id : deQuem === "sem" ? !c.responsavelId : c.responsavelId === deQuem))
    && (recorte === null || (c.status === "aberta" && (recorte === "parados" ? c.diasNaEtapa >= DIAS_PARADO : Boolean(c.tarefa?.vencida))))
    && (!procurado || [nomeDo(c), c.titulo ?? "", c.campanhaNome ?? "", c.responsavelNome ?? "", identificadorLegivel(c.identificador), identificadorLegivel(c.identificador).replace(/\D/g, "")].some((x) => x.toLocaleLowerCase("pt-BR").includes(procurado)));
  const ordenar = (a: Oportunidade, b: Oportunidade) =>
    ordem === "valor" ? (b.valorCentavos ?? -1) - (a.valorCentavos ?? -1)
      : ordem === "recente" ? b.criadaEm.localeCompare(a.criadaEm)
        : ordem === "nome" ? nomeDo(a).localeCompare(nomeDo(b), "pt-BR")
          : b.diasNaEtapa - a.diasNaEtapa;
  const visiveisAbertos = cardsAbertos.filter(passa).sort(ordenar);
  const visiveisFechados = [...ganhos, ...perdidos].filter(passa).sort((x, y) => (y.fechadaEm ?? "").localeCompare(x.fechadaEm ?? ""));
  const parados = cardsAbertos.filter((c) => c.diasNaEtapa >= DIAS_PARADO).length;
  const vencidas = cardsAbertos.filter((c) => c.tarefa?.vencida).length;

  // A coluna diz a chance da etapa e, para quem vê valor, a soma — no (i), para o cabeçalho caber numa linha.
  const colunas: ColunaDoKanban[] = [
    ...abertas.map((etapa) => {
      const soma = cardsAbertos.filter((c) => c.etapaId === etapa.id).reduce((s, c) => s + (c.valorCentavos ?? 0), 0);
      return { id: etapa.id, rotulo: etapa.nome, dica: `${etapa.probabilidadePct}% de chance nesta etapa${q.verValores && soma > 0 ? ` · ${reais(soma)}` : ""}` };
    }),
    { id: "fechadas", rotulo: "Fechadas", dica: `Ganhas e perdidas nos últimos 30 dias${q.verValores && valorGanho > 0 ? ` · ${reais(valorGanho)} ganhos` : ""}` },
  ];
  const colunaDoCelular = colunas.find((c) => c.id === colunaNoCelular)?.id ?? colunas[0]!.id;

  const nomeDaCampanha = campanhaId ? (campanhas.data?.itens ?? []).find((k) => k.id === campanhaId)?.nome ?? "uma campanha" : null;
  // O recorte em palavras, numa linha (o owner não quer quebra): o que está aberto, a previsão, e o que pede ação.
  // Ganhas e perdidas dos 30 dias ficam no `title` e na coluna Fechadas.
  const resumo = (
    <span className="hugi-funil-resumo" title={`Últimos 30 dias: ${ganhos.length} ${ganhos.length === 1 ? "ganha" : "ganhas"}${q.verValores && valorGanho > 0 ? ` (${reais(valorGanho)})` : ""}, ${perdidos.length} ${perdidos.length === 1 ? "perdida" : "perdidas"}`}>
      <span><strong>{cardsAbertos.length}</strong> {cardsAbertos.length === 1 ? "aberta" : "abertas"}</span>
      {q.verValores && q.previsaoCentavos !== null ? <span> · previsão <strong>{reais(q.previsaoCentavos)}</strong></span> : null}
      {parados > 0 ? <span className="hugi-funil-atencao"> · {parados} {parados === 1 ? "parada" : "paradas"}</span> : null}
      {vencidas > 0 ? <span className="hugi-funil-perigo" title="Com tarefa vencida"> · {vencidas} {vencidas === 1 ? "vencida" : "vencidas"}</span> : null}
      {nomeDaCampanha ? (
        <span className="hugi-funil-recorte">
          {" · "}da campanha <strong className="hugi-funil-corta">{nomeDaCampanha}</strong>
          <button type="button" className="v2-botao-icone" aria-label="Tirar o filtro da campanha" title="Ver o funil inteiro" onClick={() => setCampanhaId(null)}><X size={12} aria-hidden="true" /></button>
        </span>
      ) : null}
    </span>
  );

  const filtros = (
    /*
      NO CELULAR (H46): `min-w-0 max-w-full` na linha, nos rótulos e nos selects. Um <select> nativo tem a largura da
      MAIOR opção — a 375 px, uma campanha de nome comprido empurrava a linha 136 px para fora.
    */
    <span className="hugi-funil-filtros min-w-0 max-w-full">
      {q.quadros.length > 1 ? (
        <label className="hugi-funil-filtro min-w-0 max-w-full">
          <span className="v2-sr">Quadro</span>
          <select className="min-w-0 max-w-full" value={q.quadroId} aria-label="Quadro" onChange={(e) => { setQuadroId(e.target.value); setAberto(null); }}>
            {q.quadros.map((k) => <option key={k.id} value={k.id}>{k.nome}{k.padrao ? " (padrão)" : ""}</option>)}
          </select>
        </label>
      ) : null}
      <label className="hugi-funil-filtro min-w-0 max-w-full">
        <span className="v2-sr">Campanha</span>
        <select className="min-w-0 max-w-full" value={campanhaId ?? ""} aria-label="Filtrar por campanha" onChange={(e) => setCampanhaId(e.target.value || null)}>
          <option value="">Todas as campanhas</option>
          {(campanhas.data?.itens ?? []).map((k) => <option key={k.id} value={k.id}>{k.nome}</option>)}
        </select>
      </label>
      <label className="hugi-funil-filtro min-w-0 max-w-full">
        <span className="v2-sr">De quem é</span>
        <select className="min-w-0 max-w-full" value={deQuem} aria-label="De quem é" onChange={(e) => setDeQuem(e.target.value)}>
          <option value="todos">Todos os responsáveis</option>
          <option value="meus">Meus</option>
          <option value="sem">Sem responsável</option>
          {responsaveis.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
        </select>
      </label>
      <span className="hugi-funil-recortes" role="group" aria-label="Recorte do trabalho">
        {([["parados", `Paradas há +${DIAS_PARADO} dias`, parados], ["vencida", "Tarefa vencida", vencidas]] as const).map(([valor, rotulo, n]) => (
          <button key={valor} type="button" className={`hugi-funil-chip${recorte === valor ? " is-ativo" : ""}`} aria-pressed={recorte === valor} title={`${n} no quadro`} onClick={() => setRecorte(recorte === valor ? null : valor)}>
            {rotulo}
          </button>
        ))}
      </span>
      <label className="hugi-funil-filtro min-w-0 max-w-full">
        <span className="v2-sr">Ordenar</span>
        <select className="min-w-0 max-w-full" value={ordem} aria-label="Ordenar" onChange={(e) => setOrdem(e.target.value as Ordem)}>
          <option value="parado">Mais parado primeiro</option>
          {q.verValores ? <option value="valor">Maior valor</option> : null}
          <option value="recente">Mais recentes</option>
          <option value="nome">Contato</option>
        </select>
      </label>
    </span>
  );
  // Etapas e Importar como ícones com nome (title + aria-label): no computador o cabeçalho fica em duas linhas, não quatro.
  const secundarias = (
    <>
      {podeGerirCatalogo ? (
        <button type="button" className="v2-botao-icone hugi-funil-icone" aria-label="Etapas e motivos" title="Quadros, etapas, campos, motivos de perda e catálogo" onClick={() => setEditandoEtapas(true)}>
          <Settings2 size={16} aria-hidden="true" />
        </button>
      ) : null}
      {podeImportar ? (
        <button type="button" className="v2-botao-icone hugi-funil-icone" aria-label="Importar" title="Importar de uma planilha" onClick={() => setImportando(true)}>
          <Upload size={16} aria-hidden="true" />
        </button>
      ) : null}
    </>
  );
  const modos = (
    <nav className="hugi-funil-modos" aria-label="Forma de ver">
      {MODOS.map((m) => (
        <button key={m} type="button" aria-pressed={modo === m} onClick={() => { setModoDaUrl(null); setModo(m); }}>
          {m === "quadro" ? <SquareKanban size={14} aria-hidden="true" /> : <List size={14} aria-hidden="true" />}{m === "quadro" ? "Quadro" : "Lista"}
        </button>
      ))}
    </nav>
  );

  return (
    <PaginaInteira rotulo={titulo} classe={`hugi-funil is-${modo}`}>
      <CabecalhoDaTela
        icone={icone}
        titulo={titulo}
        classe="hugi-funil-cabecalho"
        dica={
          q.verDoTime
            ? "Quem respondeu a uma campanha já está aqui. Clique no cartão para ver a conversa e agir: mover, ganhar, perder, tarefa."
            : "Você vê as oportunidades sem responsável e as suas. Ver as do time exige read_team_funnel."
        }
        recorte={resumo}
        ferramentas={
          <>
            {modo === "quadro" ? (
              <label className="hugi-funil-busca">
                <Search size={14} aria-hidden="true" />
                <input aria-label="Buscar oportunidades" placeholder="Contato, campanha ou responsável" value={busca} onChange={(e) => setBusca(e.target.value)} />
              </label>
            ) : null}
            <span className="hugi-funil-filtros-largos">{modo === "quadro" ? filtros : null}</span>
            <span className="hugi-funil-secundarias">{secundarias}</span>
            <button type="button" className="v2-botao hugi-funil-botao-filtros" onClick={() => setFiltrosAbertos(true)}>
              <SlidersHorizontal size={14} aria-hidden="true" />Filtros
            </button>
            {modos}
          </>
        }
        primaria={<Botao onClick={() => setNovo(true)}>Nova oportunidade</Botao>}
      />

      {abertas.length === 0 ? (
        <div className="v2-cartao v2-elastico">
          <Vazio>{podeGerirCatalogo ? "Sem etapas. As etapas nascem na primeira abertura do funil. Recarregue a tela." : "Sem etapas. Peça a quem administra para criar as etapas do funil."}</Vazio>
        </div>
      ) : modo === "lista" ? (
        <ListaDoFunil quadro={q} cards={[...cardsAbertos, ...ganhos, ...perdidos]} aoAbrir={(c) => setAberto(c.id)} />
      ) : (
        <>
          {estreito ? (
            <Segmentado
              classe="hugi-funil-colunas-celular"
              rotulo="Etapa em tela"
              ativa={colunaDoCelular}
              aoTrocar={setColunaNoCelular}
              opcoes={colunas.map((c) => ({ id: c.id, rotulo: c.rotulo, contador: (c.id === "fechadas" ? visiveisFechados : visiveisAbertos.filter((x) => x.etapaId === c.id)).length }))}
            />
          ) : null}
          <Kanban<Oportunidade>
            rotulo="Etapas do funil"
            colunas={estreito ? colunas.filter((c) => c.id === colunaDoCelular) : colunas}
            itens={[...visiveisAbertos, ...visiveisFechados]}
            colunaDe={(c) => (c.status === "aberta" ? c.etapaId : "fechadas")}
            chave={(c) => c.id}
            tituloDe={nomeDo}
            cartao={(c) => <ConteudoDoCard card={c} quadro={q} aberto={aberto === c.id} />}
            aoAbrir={(c) => setAberto(c.id)}
            vazioDaColuna={procurado || recorte || deQuem !== "todos" ? "Nada neste recorte." : "Nenhuma aqui."}
            menu={(c) => (c.status === "aberta" ? null : <ReabrirBotao id={c.id} />)}
          />
        </>
      )}

      {registro ? <RegistroDoCartao key={registro.id} card={registro} quadro={q} podeTransferir={podeTransferir} aoFechar={() => setAberto(null)} /> : null}
      <Gaveta aberta={filtrosAbertos} aoFechar={() => setFiltrosAbertos(false)} titulo="Filtros do funil" largura="estreita">
        <div className="hugi-funil-filtros-gaveta">
          {filtros}
          <div className="hugi-funil-filtros-gaveta-acoes">{secundarias}</div>
        </div>
      </Gaveta>
      <Gaveta aberta={novo} aoFechar={() => setNovo(false)} titulo="Nova oportunidade">
        {novo ? <NovaOportunidade quadro={q} aoFechar={() => setNovo(false)} /> : null}
      </Gaveta>
      <Gaveta aberta={editandoEtapas && podeGerirCatalogo} aoFechar={() => setEditandoEtapas(false)} titulo="Quadros, etapas, campos, motivos de perda e catálogo" largura="extra">
        <EtapasEMotivos quadro={q} />
      </Gaveta>
      <Gaveta aberta={importando && podeImportar} aoFechar={() => setImportando(false)} titulo="Importar de uma planilha" largura="larga">
        <ImportarOportunidades quadroId={q.quadroId} />
      </Gaveta>
    </PaginaInteira>
  );
}

/** O FUNIL EM LISTA — só lê. Uma linha por cartão; a linha abre o registro do cartão (a Gaveta), onde se age. */
function ListaDoFunil(props: { readonly quadro: QuadroDoFunil; readonly cards: readonly Oportunidade[]; readonly aoAbrir: (c: Oportunidade) => void }) {
  const q = props.quadro;
  const etapa = (c: Oportunidade) => (c.status === "ganha" ? "Ganha" : c.status === "perdida" ? "Perdida" : q.etapas.find((e) => e.id === c.etapaId)?.nome ?? "—");
  const ordemDaEtapa = (c: Oportunidade) => (c.status !== "aberta" ? 999 : q.etapas.find((e) => e.id === c.etapaId)?.ordem ?? 0);
  const origem = (c: Oportunidade) => c.campanhaNome ?? ORIGEM[c.origem];
  const vistas: VistaDaLista<Oportunidade>[] = [
    { id: "abertas", rotulo: "Abertas", passa: (c) => c.status === "aberta" },
    { id: "paradas", rotulo: `Paradas há +${DIAS_PARADO} dias`, passa: (c) => c.status === "aberta" && c.diasNaEtapa >= DIAS_PARADO },
    { id: "vencidas", rotulo: "Tarefa vencida", passa: (c) => c.status === "aberta" && Boolean(c.tarefa?.vencida) },
    { id: "fechadas", rotulo: "Fechadas (30 dias)", passa: (c) => c.status !== "aberta" },
    { id: "todas", rotulo: "Todas", passa: () => true },
  ];
  const colunas: ColunaDaLista<Oportunidade>[] = [
    { id: "contato", rotulo: "Contato", principal: true, link: true, celula: nomeDo, dica: (c) => `${nomeDo(c)}${c.titulo ? ` — ${c.titulo}` : ""}`, ordenar: nomeDo },
    { id: "etapa", rotulo: "Etapa", celula: etapa, ordenar: ordemDaEtapa },
    { id: "origem", rotulo: "Origem", celula: origem, dica: origem, ordenar: origem },
    ...(q.verValores ? [{ id: "valor", rotulo: "Valor", numerica: true, celula: (c: Oportunidade) => (c.valorCentavos === null ? "—" : reais(c.valorCentavos)), ordenar: (c: Oportunidade) => c.valorCentavos }] : []),
    {
      id: "parado", rotulo: "Parado há", numerica: true,
      celula: (c) => (c.status !== "aberta" ? "—" : <span className={c.diasNaEtapa >= DIAS_PARADO ? "hugi-funil-atencao" : undefined}>{c.diasNaEtapa === 0 ? "hoje" : `${c.diasNaEtapa} d`}</span>),
      ordenar: (c) => (c.status === "aberta" ? c.diasNaEtapa : -1),
    },
    { id: "responsavel", rotulo: "Responsável", celula: (c) => c.responsavelNome ?? <span className="is-ausente">sem responsável</span>, ordenar: (c) => c.responsavelNome },
    {
      id: "tarefa", rotulo: "Próxima tarefa",
      celula: (c) => {
        if (!c.tarefa) return <span className="is-ausente">—</span>;
        const f = fraseDaTarefa(c.tarefa);
        return <span className={`hugi-funil-tarefa is-${f.tom}`}>{f.texto} · {c.tarefa.texto}</span>;
      },
      dica: (c) => (c.tarefa ? `${c.tarefa.texto} — ${new Date(c.tarefa.quando).toLocaleString("pt-BR")}` : undefined),
      ordenar: (c) => c.tarefa?.quando ?? null,
    },
  ];
  return (
    <ListView<Oportunidade>
      titulo="Oportunidades"
      icone={<List size={16} />}
      colunas={colunas}
      linhas={props.cards as Oportunidade[]}
      chave={(c) => c.id}
      vistas={vistas}
      memoria="hugi:funil:lista"
      abrir={{ href: (c) => `/funil?card=${encodeURIComponent(c.id)}`, aoAbrir: props.aoAbrir }}
      busca={{ campos: (c) => [nomeDo(c), c.titulo, c.campanhaNome, c.responsavelNome], placeholder: "Contato, campanha ou responsável" }}
      contagem={(n) => (n === 1 ? "1 oportunidade" : `${n} oportunidades`)}
      vazio={{ semNada: "Nenhuma oportunidade neste quadro.", semResultado: "Nenhuma oportunidade nesta vista." }}
    />
  );
}

/** O cartão DENSO do quadro (como o `CartaoDoQuadro` do Stonen): quem e quanto, o assunto, e os selos que decidem. */
function ConteudoDoCard(props: { readonly card: Oportunidade; readonly quadro: QuadroDoFunil; readonly aberto: boolean }) {
  const { card: c, quadro: q, aberto } = props;
  const cliente = useQueryClient();
  const nome = nomeDo(c);
  const valor = q.verValores && c.valorCentavos !== null ? <strong className="hugi-funil-card-valor">{reais(c.valorCentavos)}</strong> : null;

  if (c.status !== "aberta") {
    return (
      <span className={`hugi-funil-card is-fechado${aberto ? " is-aberto" : ""}`} data-card={c.id} data-selecionado={aberto ? "1" : undefined}>
        <span className="hugi-funil-card-linha1"><span className="hugi-funil-card-nome" title={nome}>{nome}</span>{valor}</span>
        {c.titulo ? <span className="hugi-funil-card-titulo" title={c.titulo}>{c.titulo}</span> : null}
        <span className="hugi-funil-etiquetas">
          <Selo tom={c.status === "ganha" ? "sucesso" : "perigo"}>{c.status === "ganha" ? "Ganha" : "Perdida"}</Selo>
          {c.motivoDePerda ? <span className="hugi-funil-etiqueta">{c.motivoDePerda}</span> : null}
          {c.fechadaEm ? <span className="hugi-funil-etiqueta">{new Date(c.fechadaEm).toLocaleDateString("pt-BR")}</span> : null}
          {c.campanhaNome ? <EtiquetaDaCampanha id={c.campanhaId} nome={c.campanhaNome} /> : null}
        </span>
      </span>
    );
  }

  const parado = c.diasNaEtapa >= DIAS_PARADO;
  const comQuem = comQuemEsta(c.conversa);
  return (
    <span
      className={`hugi-funil-card${aberto ? " is-aberto" : ""}`}
      data-card={c.id}
      data-selecionado={aberto ? "1" : undefined}
      onMouseEnter={() => prefetchConversa(cliente, c.id)}
      title="Abrir o cartão: a conversa e as ações"
    >
      <span className="hugi-funil-card-linha1"><span className="hugi-funil-card-nome" title={nome}>{nome}</span>{valor}</span>
      {c.titulo ? <span className="hugi-funil-card-titulo" title={c.titulo}>{c.titulo}</span> : null}
      {/* Os SELOS: de onde veio (a campanha torna o card rastreável), há quanto está parado, a tarefa, com quem está e
          o responsável. A idade do card e a temperatura ficam no registro. */}
      <span className="hugi-funil-etiquetas" aria-label="Etiquetas">
        {c.campanhaNome ? <EtiquetaDaCampanha id={c.campanhaId} nome={c.campanhaNome} /> : <span className="hugi-funil-etiqueta" title={`Origem: ${ORIGEM[c.origem]}`}>{ORIGEM[c.origem]}</span>}
        <span className={`hugi-funil-etiqueta${parado ? " is-atencao" : ""}`} title="Há quanto tempo está nesta etapa">
          <Hourglass size={10} aria-hidden="true" />{fraseDeParado(c.diasNaEtapa)}
        </span>
        {c.tarefa ? (() => {
          const f = fraseDaTarefa(c.tarefa);
          return (
            <span className={`hugi-funil-etiqueta is-tarefa-${f.tom}`} title={`${c.tarefa.texto} — ${new Date(c.tarefa.quando).toLocaleString("pt-BR")}`} data-tarefa={f.tom}>
              <CheckSquare size={10} aria-hidden="true" />{f.texto}
            </span>
          );
        })() : null}
      </span>
      <span className="hugi-funil-card-linha">
        <Selo tom={TOM_DO_SELO[comQuem.tom]}>{comQuem.rotulo}</Selo>
        <span className={c.responsavelNome ? "hugi-funil-corta" : "hugi-funil-corta is-ausente"}>{c.responsavelNome ?? "sem responsável"}</span>
      </span>
    </span>
  );
}

/*
  A ETIQUETA DA CAMPANHA abre a campanha (spec 23, jornada 5). Ela mora DENTRO do botão que abre a conversa (o card do
  Kanban), e botão não contém link: o clique na etiqueta é interceptado (não abre a conversa) e leva a
  `/campanhas/<id>`. Para teclado e leitor de tela, o mesmo destino é um link de verdade na gaveta de detalhe e no
  cabeçalho da conversa.
*/
function EtiquetaDaCampanha(props: { readonly id: string | null; readonly nome: string }) {
  const [, navegar] = useLocation();
  const abrir = (e: MouseEvent) => {
    if (!props.id) return;
    e.preventDefault();
    e.stopPropagation();
    navegar(`/campanhas/${encodeURIComponent(props.id)}`);
  };
  return (
    <span
      className={`etiqueta-campanha hugi-funil-etiqueta${props.id ? " is-link" : ""}`}
      data-campanha={props.id ?? ""}
      title={props.id ? `Veio da campanha ${props.nome} — abrir a campanha` : `Veio da campanha ${props.nome}`}
      onClick={abrir}
    >
      <span aria-hidden="true">📣</span>
      <span className="hugi-funil-corta">{props.nome}</span>
    </span>
  );
}

/** O link acessível para a campanha de onde o card veio. */
function LinkDaCampanha(props: { readonly id: string | null; readonly nome: string }) {
  const [, navegar] = useLocation();
  if (!props.id) return <strong>{props.nome}</strong>;
  const href = `/campanhas/${encodeURIComponent(props.id)}`;
  return (
    <a className="v2-link" href={href} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); navegar(href); }}>
      {props.nome}
    </a>
  );
}

const TOM_DO_SELO: Readonly<Record<"ok" | "degradado" | "ruim" | "neutro", Tom>> = { ok: "sucesso", degradado: "atencao", ruim: "perigo", neutro: "neutro" };

/*
  O REGISTRO DO CARTÃO — na Gaveta ao lado do quadro (como o `RegistroDoCartao` do Stonen), só quando há cartão
  escolhido. A Gaveta não rola inteira: no topo os selos e as abas Conversa | Detalhes; só o painel da aba rola; no pé,
  FIXA, a barra de ações — mover, perder (o motivo é obrigatório: perda sem motivo não ensina nada) e Ganhar, a única
  primária (spec 23, jornada 5: quem abre o cartão quase sempre vem fechá-lo). Cartão fechado: no pé, Reabrir.
*/
function useAgir() {
  const [recado, setRecado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const agir = async (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => {
    setOcupado(true);
    setRecado(null);
    const r = await fn();
    setOcupado(false);
    if (!r.ok) setRecado(mensagemDa(r.falha) ?? "Não deu certo. Tente de novo.");
  };
  return { recado, ocupado, agir };
}

function RegistroDoCartao(props: { readonly card: Oportunidade; readonly quadro: QuadroDoFunil; readonly podeTransferir: boolean; readonly aoFechar: () => void }) {
  const { card: c, quadro: q } = props;
  const cliente = useQueryClient();
  const [aba, setAba] = useState<"conversa" | "detalhes">("conversa");
  const [motivo, setMotivo] = useState("");
  const { recado, ocupado, agir } = useAgir();
  const nome = nomeDo(c);
  const etapa = q.etapas.find((e) => e.id === c.etapaId);
  const outras = q.etapas.filter((e) => e.tipo === "aberta" && e.id !== c.etapaId);
  const comQuem = comQuemEsta(c.conversa);

  const rodape = c.status !== "aberta" ? (
    <div className="hugi-funil-registro-acoes"><ReabrirBotao id={c.id} /></div>
  ) : (
    <div className="hugi-funil-registro-acoes" role="group" aria-label="Fechar a oportunidade">
      {outras.length > 0 ? (
        <select
          className="campo compacto"
          value=""
          disabled={ocupado}
          aria-label={`Mover ${nome} para`}
          onChange={(e) => { const id = e.target.value; if (id) void agir(() => moverOportunidade(cliente, c.id, id)); }}
        >
          <option value="">Mover para…</option>
          {outras.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
      ) : null}
      <span className="hugi-funil-registro-perder">
        <select className="campo compacto" value={motivo} onChange={(e) => setMotivo(e.target.value)} aria-label="Motivo da perda">
          <option value="">Motivo da perda…</option>
          {q.motivosDePerda.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
        </select>
        <Botao tipo="perigo" desabilitado={ocupado || motivo === ""} motivoDeDesabilitar="Escolha o motivo da perda — perda sem motivo não ensina nada." onClick={() => void agir(() => perderOportunidade(cliente, c.id, motivo))}>
          Perder
        </Botao>
      </span>
      <Botao desabilitado={ocupado} onClick={() => void agir(() => ganharOportunidade(cliente, c.id))}>Ganhar</Botao>
      {recado ? <p role="alert" className="hugi-funil-recado">{recado}</p> : null}
    </div>
  );

  return (
    <Gaveta aberta aoFechar={props.aoFechar} titulo={`Detalhes de ${nome}`} largura="larga" classe="hugi-funil-registro" rodape={rodape}>
      <div className="hugi-funil-registro-topo">
        {c.titulo ? <p className="hugi-funil-registro-titulo">{c.titulo}</p> : null}
        <span className="hugi-funil-etiquetas">
          {c.status === "aberta" ? <Selo tom="neutro">{etapa?.nome ?? "—"}</Selo> : <Selo tom={c.status === "ganha" ? "sucesso" : "perigo"}>{c.status === "ganha" ? "Ganha" : "Perdida"}</Selo>}
          <Selo tom={TOM_DO_SELO[comQuem.tom]}>{comQuem.rotulo}</Selo>
          {c.status === "aberta" ? <span className={`hugi-funil-etiqueta${c.diasNaEtapa >= DIAS_PARADO ? " is-atencao" : ""}`}><Hourglass size={10} aria-hidden="true" />{fraseDeParado(c.diasNaEtapa)}</span> : null}
          <span className="hugi-funil-etiqueta" title={`Criado em ${new Date(c.criadaEm).toLocaleDateString("pt-BR")}`}><Clock size={10} aria-hidden="true" />{fraseDeIdade(c.criadaEm)}</span>
          {q.verValores && c.valorCentavos !== null ? <span className="hugi-funil-etiqueta"><strong>{reais(c.valorCentavos)}</strong></span> : null}
          {c.temperatura ? <span className="hugi-funil-etiqueta">{TEMPERATURA[c.temperatura]}</span> : null}
          <span className="hugi-funil-etiqueta">{c.responsavelNome ?? "sem responsável"}</span>
        </span>
        <span className="hugi-funil-registro-links">
          {c.campanhaNome ? <span className="hugi-funil-fraco">Veio da campanha <LinkDaCampanha id={c.campanhaId} nome={c.campanhaNome} />.</span> : <span className="hugi-funil-fraco">Origem: {ORIGEM[c.origem]}.</span>}
          {c.atendimentoId ? <a className="v2-link" href={`/atender/${c.atendimentoId}`}>Abrir a conversa{c.protocolo ? ` ${c.protocolo}` : ""}</a> : null}
        </span>
      </div>
      <Segmentado
        classe="hugi-funil-registro-abas"
        rotulo="Seções do cartão"
        ativa={aba}
        aoTrocar={(id) => setAba(id as "conversa" | "detalhes")}
        opcoes={[{ id: "conversa", rotulo: "Conversa", controla: "hugi-funil-registro-painel" }, { id: "detalhes", rotulo: "Detalhes", controla: "hugi-funil-registro-painel" }]}
      />
      <div id="hugi-funil-registro-painel" className={`hugi-funil-registro-painel is-${aba}`} role="tabpanel" aria-label={aba === "conversa" ? "Conversa" : "Detalhes"}>
        {aba === "conversa" ? <ConversaDoCardAberto card={c} /> : <DetalhesDoCartao card={c} quadro={q} podeTransferir={props.podeTransferir} />}
      </div>
    </Gaveta>
  );
}

/** A aba Detalhes do registro: passar para a fila, tarefas, campos, itens e valor. O desfecho mora no pé da Gaveta. */
function DetalhesDoCartao(props: { readonly card: Oportunidade; readonly quadro: QuadroDoFunil; readonly podeTransferir: boolean }) {
  const { card: c, quadro: q } = props;
  const cliente = useQueryClient();
  const { recado, ocupado, agir } = useAgir();
  const [valor, setValor] = useState<string>(c.valorCentavos === null ? "" : String(c.valorCentavos / 100));

  return (
    <div className="hugi-funil-detalhe">
      {c.conversa && (c.conversa.comQuem === "ia" || c.conversa.comQuem === "integracao" || c.conversa.comQuem === "humano") ? (
        <div>
          <Botao
            tipo="secundario"
            desabilitado={ocupado || !props.podeTransferir}
            motivoDeDesabilitar={props.podeTransferir ? "Aguarde." : "Passar para a fila é transferir — exige a permissão transfer_conversation."}
            onClick={() => void agir(() => passarParaAFila(cliente, c.id))}
          >
            {c.conversa.comQuem === "ia" ? "Tirar da IA e passar para a fila" : "Passar para a fila"}
          </Botao>
        </div>
      ) : null}
      {c.observacao ? <p>{c.observacao}</p> : null}
      <TarefasDoCard card={c} agir={agir} ocupado={ocupado} />
      {q.campos.length > 0 ? <CamposDoCard card={c} campos={q.campos} agir={agir} ocupado={ocupado} /> : null}
      {q.verValores ? <ItensDoCard card={c} agir={agir} ocupado={ocupado} /> : null}
      {q.verValores ? (
        <label className="flex items-center gap-1" title={c.itens > 0 ? "Com itens do catálogo, o valor é a soma deles." : undefined}>
          Valor R$
          <input
            className="campo compacto w-24"
            inputMode="decimal"
            disabled={c.itens > 0}
            aria-label={c.itens > 0 ? "Valor (soma dos itens)" : "Valor"}
            value={c.itens > 0 && c.valorCentavos !== null ? String(c.valorCentavos / 100) : valor}
            onChange={(e) => setValor(e.target.value)}
            onBlur={() => {
              const n = Number(valor.replace(",", "."));
              const centavos = valor.trim() === "" ? null : Number.isFinite(n) ? Math.round(n * 100) : null;
              if (centavos !== c.valorCentavos) void agir(() => editarOportunidade(cliente, c.id, { valorCentavos: centavos }));
            }}
          />
        </label>
      ) : null}
      {recado ? <p role="alert" className="hugi-funil-recado">{recado}</p> : null}
    </div>
  );
}

const TIPO_DE_CAMPO: Record<CampoDoFunil["tipo"], string> = { texto: "Texto", numero: "Número", data: "Data", opcao: "Opção" };

/**
 * Os campos personalizados do quadro, no card (0069). Cada um grava sozinho ao sair do campo — é um
 * `PATCH campos: {id: valor}`; o servidor valida pelo tipo e a recusa vem como recado.
 */
function CamposDoCard(props: { readonly card: Oportunidade; readonly campos: readonly CampoDoFunil[]; readonly agir: (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => Promise<void>; readonly ocupado: boolean }) {
  const { card: c } = props;
  const cliente = useQueryClient();
  const [valores, setValores] = useState<Record<string, string>>(() => Object.fromEntries(props.campos.map((f) => [f.id, c.campos[f.id] === null || c.campos[f.id] === undefined ? "" : String(c.campos[f.id])])));
  const gravar = (f: CampoDoFunil, v: string) => {
    const atual = c.campos[f.id];
    const antes = atual === null || atual === undefined ? "" : String(atual);
    if (v === antes) return;
    void props.agir(() => editarOportunidade(cliente, c.id, { campos: { [f.id]: v === "" ? null : f.tipo === "numero" ? Number(v.replace(",", ".")) : v } }));
  };
  return (
    <section className="flex flex-col gap-1" aria-label="Campos do card">
      {props.campos.map((f) => (
        <label key={f.id} className="flex items-center gap-1">
          <span className="w-24 shrink-0 truncate" title={f.nome}>{f.nome}</span>
          {f.tipo === "opcao" ? (
            <select className="campo compacto min-w-0 flex-1" aria-label={f.nome} value={valores[f.id] ?? ""} disabled={props.ocupado} onChange={(e) => { setValores({ ...valores, [f.id]: e.target.value }); gravar(f, e.target.value); }}>
              <option value="">—</option>
              {f.opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input
              className="campo compacto min-w-0 flex-1"
              aria-label={f.nome}
              type={f.tipo === "data" ? "date" : "text"}
              inputMode={f.tipo === "numero" ? "decimal" : undefined}
              value={valores[f.id] ?? ""}
              disabled={props.ocupado}
              onChange={(e) => setValores({ ...valores, [f.id]: e.target.value })}
              onBlur={(e) => gravar(f, e.target.value)}
            />
          )}
        </label>
      ))}
    </section>
  );
}

/**
 * Os itens do catálogo que compõem o card (spec 10 §6, 0067). Com itens, o valor é a soma deles — o
 * campo de valor fica desabilitado e diz por quê. Só para quem vê valores: compor é mudar o valor.
 */
function ItensDoCard(props: { readonly card: Oportunidade; readonly agir: (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => Promise<void>; readonly ocupado: boolean }) {
  const { card: c } = props;
  const cliente = useQueryClient();
  const itens = useItensDaOportunidade(c.id);
  const catalogo = useCatalogoDoFunil();
  const [itemId, setItemId] = useState("");
  const [quantidade, setQuantidade] = useState("1");
  const lista = itens.data?.itens ?? [];
  const vivos = catalogo.data?.itens ?? [];
  return (
    <section className="flex flex-col gap-1" aria-label="Itens do card">
      <h4 className="font-medium">Itens</h4>
      {lista.map((i) => (
        <div key={i.id} className="flex items-center gap-1" data-item-do-card={i.id}>
          <span className="min-w-0 flex-1 truncate">{i.quantidade} × {i.nome}{i.sku ? <span className="text-[color:var(--tinta-fraca)]"> ({i.sku})</span> : null}</span>
          {i.totalCentavos !== null ? <span>{reais(i.totalCentavos)}</span> : null}
          {c.status === "aberta" ? <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Tirar ${i.nome} do card`} disabled={props.ocupado} onClick={() => void props.agir(() => removerItemDoCard(cliente, c.id, i.id))}>Tirar</button> : null}
        </div>
      ))}
      {itens.isSuccess && lista.length === 0 ? <p className="text-[color:var(--tinta-fraca)]">Sem itens — o valor é digitado. {vivos.length === 0 ? "Cadastre produtos e serviços em Etapas e motivos." : ""}</p> : null}
      {c.status === "aberta" && vivos.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          <select className="campo compacto min-w-0 flex-1" aria-label="Item do catálogo" value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">Acrescentar do catálogo…</option>
            {vivos.map((i) => <option key={i.id} value={i.id}>{i.nome} — {reais(i.precoCentavos)}</option>)}
          </select>
          <input className="campo compacto w-14" inputMode="numeric" aria-label="Quantidade" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
          <Botao tipo="secundario" desabilitado={props.ocupado || itemId === "" || !(Number(quantidade) >= 1)} motivoDeDesabilitar="Escolha o item e a quantidade." onClick={() => void props.agir(async () => { const r = await adicionarItemNoCard(cliente, c.id, { itemId, quantidade: Math.round(Number(quantidade)) }); if (r.ok) { setItemId(""); setQuantidade("1"); } return r; })}>Acrescentar</Botao>
        </div>
      ) : null}
    </section>
  );
}

/**
 * As tarefas do card — a agenda, com o card como dono (spec 10 §6). "Ligar amanhã às 10" nasce aqui
 * e aparece na tela de Agenda igual a qualquer lembrete; concluir e cancelar são os mesmos da agenda.
 */
function TarefasDoCard(props: { readonly card: Oportunidade; readonly agir: (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => Promise<void>; readonly ocupado: boolean }) {
  const { card: c } = props;
  const cliente = useQueryClient();
  const tarefas = useTarefasDaOportunidade(c.id);
  const [texto, setTexto] = useState("");
  const [quando, setQuando] = useState("");
  const lista = tarefas.data?.tarefas ?? [];
  const pendentes = lista.filter((t) => t.estado === "pendente");
  const feitas = lista.filter((t) => t.estado !== "pendente");
  const criar = () => {
    if (texto.trim() === "" || quando === "") return;
    void props.agir(async () => {
      const r = await criarTarefa(cliente, c.id, { texto: texto.trim(), quando: new Date(quando).toISOString() });
      if (r.ok) { setTexto(""); setQuando(""); }
      return r;
    });
  };
  return (
    <section className="flex flex-col gap-1" aria-label="Tarefas">
      <h4 className="font-medium">Tarefas</h4>
      {tarefas.isSuccess && lista.length === 0 ? <p className="text-[color:var(--tinta-fraca)]">Nenhuma tarefa. A que você criar aqui aparece também na Agenda.</p> : null}
      {pendentes.map((t) => (
        <div key={t.id} className="flex items-start gap-1" data-tarefa-pendente={t.id}>
          <span className="min-w-0 flex-1">
            <span className="block">{t.texto}</span>
            <span className="block text-[color:var(--tinta-fraca)]" style={t.vencida ? { color: "var(--erro-texto)" } : undefined}>
              {new Date(t.quando).toLocaleString("pt-BR")}{t.vencida ? " · vencida" : ""}{t.paraAtendenteNome ? ` · ${t.paraAtendenteNome}` : ""}
            </span>
          </span>
          {/* Secundário: na gaveta a única primária é Ganhar (spec 23, jornada 5). */}
          <Botao tipo="secundario" desabilitado={props.ocupado} onClick={() => void props.agir(() => concluirTarefa(cliente, c.id, t.id))}>Feito</Botao>
          <Botao tipo="secundario" desabilitado={props.ocupado} onClick={() => void props.agir(() => cancelarTarefa(cliente, c.id, t.id))}>Cancelar</Botao>
        </div>
      ))}
      {feitas.length > 0 ? <p className="text-[color:var(--tinta-fraca)]">{feitas.length} concluída(s) ou cancelada(s).</p> : null}
      {c.status === "aberta" ? (
        <form className="flex flex-wrap items-center gap-1" onSubmit={(e) => { e.preventDefault(); criar(); }}>
          <input className="campo compacto min-w-0 flex-1" placeholder="Nova tarefa (ex.: ligar para confirmar)" aria-label="Nova tarefa" value={texto} onChange={(e) => setTexto(e.target.value)} />
          <input className="campo compacto" type="datetime-local" aria-label="Quando" value={quando} onChange={(e) => setQuando(e.target.value)} />
          <Botao tipo="secundario" desabilitado={props.ocupado || texto.trim() === "" || quando === ""} motivoDeDesabilitar="Escreva a tarefa e escolha quando." onClick={criar}>Criar tarefa</Botao>
        </form>
      ) : null}
    </section>
  );
}

/**
 * A conversa da campanha, ao lado do quadro — SÓ ela. O servidor devolve os atendimentos que a
 * campanha do card abriu para este contato; outras conversas fechadas do mesmo contato não vêm.
 */
function ConversaDoCardAberto(props: { readonly card: Oportunidade | null }) {
  const conversa = useConversaDaOportunidade(props.card?.id ?? null);
  const cliente = useQueryClient();
  const sessao = useSessao();
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  // Fora da janela de 24h o campo de texto NÃO faz nada neste canal (§7.2 da spec 05): quem decide é o
  // domínio, e a tela troca o compositor pelo seletor de template quando a recusa vem com esse motivo.
  const [janelaFechada, setJanelaFechada] = useState<string | null>(null);
  const c = props.card;
  const podeResponder = sessao.permissoes.includes("respond_conversation");
  const temAberta = c !== null && c.conversa !== null && c.conversa.comQuem !== "finalizada";
  const depoisDeEnviar = async () => {
    setTexto("");
    setJanelaFechada(null);
    if (c) await cliente.invalidateQueries({ queryKey: ["funil", "conversa", c.id] });
  };
  const enviar = async () => {
    const t = texto.trim();
    if (!c || !t || enviando) return;
    setEnviando(true);
    setRecado(null);
    const r = await responderPelaOportunidade(cliente, c.id, { texto: t });
    setEnviando(false);
    if (r.ok) {
      await depoisDeEnviar();
      return;
    }
    const detalhe = r.falha?.erro.detalhe as { motivo?: string } | undefined;
    if (detalhe?.motivo === "janela_de_resposta_expirada") {
      setJanelaFechada(mensagemDa(r.falha) ?? "A janela de 24 horas deste canal fechou.");
      return;
    }
    setRecado(mensagemDa(r.falha) ?? "Não deu para enviar. Tente de novo.");
  };
  const enviarTemplate = async (t: { nome: string; idioma: string; variaveis: string[] }): Promise<string | null> => {
    if (!c) return "Escolha um card.";
    const r = await responderPelaOportunidade(cliente, c.id, { template: t });
    if (r.ok) {
      await depoisDeEnviar();
      return null;
    }
    return mensagemDa(r.falha) ?? "Não deu para enviar o template. Tente de novo.";
  };

  // Mora na aba Conversa do REGISTRO DO CARTÃO (a Gaveta), não mais numa coluna fixa ao lado do quadro (01/11).
  return (
    <aside className="v2-cartao hugi-funil-conversa flex min-h-0 flex-col overflow-hidden text-xs" aria-label="Conversa da campanha">
      {/* O cabeçalho da conversa, como no inbox: quem, e com quem está. */}
      <header className="flex items-start justify-between gap-2 border-b border-[color:var(--borda)] px-3 py-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-medium">{c ? (c.contatoNome ?? identificadorLegivel(c.identificador)) : "Conversa"}</h2>
          <p className="text-[color:var(--tinta-fraca)]">
            {!c
              ? "Clique num card para ver a conversa dele aqui."
              : c.campanhaNome
                ? <>Só o que foi da campanha <LinkDaCampanha id={c.campanhaId} nome={c.campanhaNome} />.</>
                : c.atendimentoId
                  ? "A conversa que abriu este card."
                  : "A conversa aberta deste contato."}
          </p>
        </div>
        {c ? (
          <div className="flex shrink-0 items-center gap-2">
            <Selo tom={TOM_DO_SELO[comQuemEsta(c.conversa).tom]}>{comQuemEsta(c.conversa).rotulo}</Selo>
          </div>
        ) : null}
      </header>

      {/* A SUPERFÍCIE DA CONVERSA — o véu quente e o grafismo da marca, os mesmos do inbox (Fio.tsx). */}
      <div
        className="relative min-h-0 flex-1 overflow-y-auto px-3 py-3"
        style={{
          backgroundImage: "var(--grafismo), linear-gradient(to bottom, var(--conversa-topo), var(--conversa-base))",
          backgroundSize: "340px 340px, auto",
          backgroundRepeat: "repeat, no-repeat",
        }}
      >
        {!c ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-[color:var(--tinta)]">
            <MessageSquareText size={28} aria-hidden="true" />
            <p className="text-sm">Nenhum card escolhido.</p>
            <p>A conversa do card aparece aqui, e dá para responder sem sair do funil.</p>
          </div>
        ) : conversa.isLoading ? (
          <EstadoCarregando />
        ) : conversa.isError ? (
          <EstadoDaFalha falha={falhaDe(conversa.error)!} aoRepetir={() => void conversa.refetch()} />
        ) : conversa.data && conversa.data.atendimentos.length === 0 ? (
          <p role="status" className="py-6 text-center text-sm text-[color:var(--tinta)]">
            {c.campanhaNome ? "Esta campanha ainda não abriu conversa com este contato." : "Este contato não tem conversa aberta."}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {conversa.data?.atendimentos.map((a) => (
              <section key={a.id} className="flex flex-col gap-2.5" aria-label={`Atendimento ${a.protocolo}`}>
                <div className="self-center rounded-full bg-[color:var(--realce)] px-2.5 py-0.5 text-[11px] text-[color:var(--tinta-fraca)]">
                  <a className="text-[color:var(--acao-texto)] underline" href={`/atender/${a.id}`}>{a.protocolo}</a>
                  {" · "}{a.estado === "finalizado" ? `fechada${a.motivo ? ` (${a.motivo})` : ""}` : "aberta"}
                </div>
                {!a.visivel ? (
                  <p className="text-center text-[color:var(--tinta)]">Fora do seu recorte: ler conversas fechadas exige read_conversation_history.</p>
                ) : null}
                <Linhas mensagens={a.mensagens} />
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Responder daqui é ASSUMIR e enviar — o mesmo caminho do inbox. Fora da janela de 24h a recusa
          vem do servidor com o motivo, e o compositor vira o seletor de template. */}
      {c && janelaFechada && c.conversa?.conexaoId ? (
        <SeletorDeTemplateDoKanban conexaoId={c.conversa.conexaoId} explicacao={janelaFechada} aoEnviar={enviarTemplate} aoVoltar={() => setJanelaFechada(null)} />
      ) : (
        <form
          className="flex flex-col gap-1 border-t border-[color:var(--borda)] px-3 py-2"
          onSubmit={(e) => { e.preventDefault(); void enviar(); }}
          aria-label="Responder"
        >
          <textarea
            className="campo compacto min-h-[3.5rem] w-full resize-y"
            placeholder={!c ? "Escolha um card para responder." : temAberta ? "Responder por aqui assume a conversa. Enter envia, Shift+Enter quebra a linha." : "Sem conversa aberta: abra o atendimento para falar com esta pessoa."}
            value={texto}
            disabled={!c || !podeResponder || !temAberta || enviando}
            aria-label="Resposta"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void enviar(); } }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[color:var(--tinta-fraca)]">
              {c?.conversa?.comQuem === "ia" ? "A IA sai da conversa quando você responde." : c?.conversa?.comQuem === "humano" && c.conversa.donoNome ? `Está com ${c.conversa.donoNome}.` : ""}
            </span>
            {/* Secundário: no registro do cartão a única primária é Ganhar (uma primária por tela); Enter envia. */}
            <Botao
              tipo="secundario"
              desabilitado={!c || !podeResponder || !temAberta || enviando || texto.trim() === ""}
              motivoDeDesabilitar={!c ? "Escolha um card." : !podeResponder ? "Responder exige a permissão respond_conversation." : !temAberta ? "Sem conversa aberta." : "Escreva algo antes de enviar."}
              onClick={() => void enviar()}
            >
              {enviando ? "Enviando…" : "Enviar"}
            </Botao>
          </div>
          {recado ? <p role="alert" style={{ color: "var(--erro-texto)" }}>{recado}</p> : null}
        </form>
      )}
    </aside>
  );
}

const STATUS_DE_ENVIO: Readonly<Record<NonNullable<MensagemDaConversaDoCard["statusDeEnvio"]>, string>> = {
  enfileirado: "Enviando",
  sent: "Enviado",
  delivered: "Entregue",
  read: "Lido",
  failed: "Não enviado",
};

function rotuloDoDia(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return "Hoje";
  if (d.toDateString() === ontem.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR");
}

/**
 * As bolhas — a forma do fio do inbox (`web/inbox/src/Fio.tsx`): recebida à esquerda no papel, enviada
 * à direita no azul claro, eco no âmbar; o separador de dia; o autor ACIMA da bolha; hora e estado
 * do envio fora do balão, em `--tinta` (a tinta fraca reprova sobre o véu). Copiado, e não importado:
 * o inbox é outro build, e o que ele tem de compartilhado já está no `@hugi/ui` (`TextoDaMensagem`).
 */
function Linhas(props: { readonly mensagens: readonly MensagemDaConversaDoCard[] }) {
  if (props.mensagens.length === 0) {
    return <p role="status" className="py-4 text-center text-sm text-[color:var(--tinta)]">Nenhuma mensagem neste atendimento ainda.</p>;
  }
  return (
    <ol className="flex flex-col gap-2.5">
      {props.mensagens.map((m, i) => {
        const recebida = m.direcao === "recebida";
        const anterior = props.mensagens[i - 1];
        const mudouODia = i === 0 || (m.ocorridoEm && anterior?.ocorridoEm && new Date(m.ocorridoEm).toDateString() !== new Date(anterior.ocorridoEm).toDateString());
        return (
          <li key={m.id} className="contents">
            {mudouODia && m.ocorridoEm ? (
              <span className="self-center rounded-full bg-[color:var(--realce)] px-2.5 py-0.5 text-[11px] text-[color:var(--tinta-fraca)]">{rotuloDoDia(m.ocorridoEm)}</span>
            ) : null}
            {/* A bolha usa a largura da gaveta (owner, 01/11: "muitas quebras de linha e não aproveita o espaço"): os
                15rem eram da coluna estreita de antes. O teto mora AQUI, no item da coluna — num filho de largura
                automática, a porcentagem era do próprio texto e a bolha continuava estreita. */}
            <div className={[recebida ? "self-start" : "self-end", "max-w-[min(42rem,85%)]"].join(" ")}>
              <div>
                {!recebida && (m.enviadaPorNome || m.direcao === "eco") ? (
                  <p className="mb-1 flex items-center justify-end gap-1.5 px-1 text-[11px]">
                    <span className="font-semibold text-[color:var(--tinta-forte)]">{m.direcao === "eco" ? "Pelo celular" : m.enviadaPorNome}</span>
                  </p>
                ) : null}
                <div className={["rounded-2xl px-3.5 py-2 shadow-sm text-sm", recebida ? "rounded-bl-sm bg-[color:var(--papel)]" : m.direcao === "eco" ? "rounded-br-sm bg-[color:var(--atencao-clara)]" : "rounded-br-sm bg-[color:var(--acao-clara)]"].join(" ")}>
                  {m.midia && !m.texto ? (
                    <span className="text-[color:var(--tinta-fraca)]">{m.midia.transcricao ?? `[${m.midia.nome ?? m.midia.mime ?? "anexo"}]`}</span>
                  ) : (
                    <TextoDaMensagem texto={m.texto} expiraEm={m.expiraEm} />
                  )}
                </div>
                <p className={["mt-0.5 flex items-center gap-1.5 px-1 text-[11px] text-[color:var(--tinta)]", recebida ? "" : "justify-end"].join(" ")}>
                  {m.ocorridoEm ? <time dateTime={m.ocorridoEm}>{new Date(m.ocorridoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</time> : null}
                  {m.direcao === "eco" ? <SeloDoHugi estado="degradado" rotulo="Eco" /> : null}
                  {m.statusDeEnvio ? <SeloDoHugi estado={m.statusDeEnvio === "failed" ? "ruim" : m.statusDeEnvio === "read" ? "ok" : "neutro"} rotulo={STATUS_DE_ENVIO[m.statusDeEnvio]} /> : null}
                </p>
                {m.statusDeEnvio === "failed" && m.erroDeEnvio ? (
                  <p role="alert" className="mt-1 rounded px-2 py-1 text-right text-[11px]" style={{ background: "var(--erro-clara)", color: "var(--tinta-forte)" }}>{m.erroDeEnvio}</p>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * O seletor de template do kanban — a forma do inbox (§7.2 da spec 05): OUTRO controle no lugar da
 * caixa de texto, não um aviso acima dela. Só os aprovados da conexão da conversa; marketing é
 * cobrado sempre, e a tela diz.
 */
function SeletorDeTemplateDoKanban(props: {
  readonly conexaoId: string;
  readonly explicacao: string;
  readonly aoEnviar: (t: { nome: string; idioma: string; variaveis: string[] }) => Promise<string | null>;
  readonly aoVoltar: () => void;
}) {
  const templates = useTemplates(true);
  const [escolhido, setEscolhido] = useState<Template | null>(null);
  const [variaveis, setVariaveis] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const daConexao = (templates.data?.templates ?? []).filter((t) => t.conexaoId === props.conexaoId);
  const aprovados = daConexao.filter((t) => t.estado === "aprovado" && !t.bloqueio);

  const mandar = async () => {
    if (!escolhido || enviando) return;
    setEnviando(true);
    setErro(null);
    const falha = await props.aoEnviar({ nome: escolhido.nome, idioma: escolhido.idioma, variaveis });
    setEnviando(false);
    if (falha) setErro(falha);
  };

  return (
    <div className="vidro-controle flex flex-col gap-2 border-t border-[color:var(--borda)] p-2" aria-label="Fora da janela: escolha um template">
      <p className="font-medium">A janela de 24 horas fechou — só template sai.</p>
      <p className="text-[color:var(--tinta-fraca)]">{props.explicacao}</p>
      {templates.isPending ? <p className="text-[color:var(--tinta-fraca)]">Carregando os templates…</p> : null}
      {templates.data && aprovados.length === 0 ? (
        <p className="text-[color:var(--tinta-fraca)]">Esta conexão não tem template aprovado. Sincronize em Canais.</p>
      ) : null}
      {!escolhido ? (
        <ul className="flex flex-col gap-1">
          {aprovados.map((t) => (
            <li key={t.id}>
              <button type="button" className="w-full text-left" onClick={() => { setEscolhido(t); setVariaveis(Array.from({ length: t.variaveis }, () => "")); }}>
                <code>{t.nome}</code> ({t.idioma}){t.sempreCobrado ? <span style={{ color: "var(--atencao-texto)" }}> · marketing, cobrado</span> : null}
                {t.corpo ? <span className="block truncate text-[color:var(--tinta-fraca)]">{t.corpo}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col gap-1">
          <p><code>{escolhido.nome}</code>: {escolhido.corpo}</p>
          {variaveis.map((v, i) => (
            <label key={i} className="flex items-center gap-1">
              {`{{${i + 1}}}`}
              <input className="campo compacto flex-1" value={v} aria-label={`Variável ${i + 1}`} onChange={(e) => setVariaveis(variaveis.map((x, j) => (j === i ? e.target.value : x)))} />
            </label>
          ))}
          <div className="flex gap-2">
            <Botao desabilitado={enviando || variaveis.some((v) => v.trim() === "")} motivoDeDesabilitar="Preencha todas as variáveis." onClick={() => void mandar()}>
              {enviando ? "Enviando…" : "Enviar template"}
            </Botao>
            <Botao tipo="secundario" onClick={() => setEscolhido(null)}>Outro</Botao>
          </div>
        </div>
      )}
      {erro ? <p role="alert" style={{ color: "var(--erro-texto)" }}>{erro}</p> : null}
      <button type="button" className="self-start text-[color:var(--acao-texto)] underline" onClick={props.aoVoltar}>Voltar ao texto</button>
    </div>
  );
}

/**
 * As etapas e os motivos de perda, pela tela (spec 10 — só a API fazia). `manage_catalog`, como
 * filas e motivos de encerramento. Arquivar, nunca apagar: card e histórico apontam para a etapa.
 */
function EtapasEMotivos(props: { readonly quadro: QuadroDoFunil }) {
  const cliente = useQueryClient();
  const [recado, setRecado] = useState<string | null>(null);
  const [novaEtapa, setNovaEtapa] = useState({ nome: "", probabilidadePct: "50" });
  const [novoMotivo, setNovoMotivo] = useState("");
  const [novoItem, setNovoItem] = useState({ nome: "", sku: "", preco: "" });
  const [novoQuadro, setNovoQuadro] = useState("");
  const [novoCampo, setNovoCampo] = useState<{ nome: string; tipo: CampoDoFunil["tipo"]; opcoes: string }>({ nome: "", tipo: "texto", opcoes: "" });
  const catalogo = useCatalogoDoFunil();
  const abertas = props.quadro.etapas.filter((e) => e.tipo === "aberta");
  const agir = async (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => {
    setRecado(null);
    const r = await fn();
    if (!r.ok) setRecado(mensagemDa(r.falha) ?? "Não deu certo. Tente de novo.");
    return r.ok;
  };
  return (
    <section className="flex flex-col gap-3 text-xs" aria-label="Etapas e motivos de perda">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Quadros</h3>
          <p className="text-[color:var(--tinta-fraca)]">Um quadro por processo — Vendas, Pós-venda. Cada um tem as próprias etapas e campos; um contato pode ter um card aberto em cada. O <strong>padrão</strong> é onde os leads de campanha e anúncio nascem.</p>
          <ul className="flex flex-col gap-1">
            {props.quadro.quadros.map((k) => (
              <li key={k.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>{k.nome}{k.padrao ? <span className="text-[color:var(--tinta-fraca)]"> · padrão</span> : null}{k.id === props.quadro.quadroId ? <span className="text-[color:var(--tinta-fraca)]"> · aberto</span> : null}</span>
                <span className="flex gap-2">
                  {!k.padrao ? <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Tornar ${k.nome} o quadro padrão`} onClick={() => void agir(() => editarQuadro(cliente, k.id, { padrao: true }))}>Tornar padrão</button> : null}
                  {!k.padrao ? <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Arquivar o quadro ${k.nome}`} onClick={() => void agir(() => arquivarQuadro(cliente, k.id))}>Arquivar</button> : null}
                </span>
              </li>
            ))}
          </ul>
          <form className="flex flex-wrap items-end gap-1.5" onSubmit={(ev) => { ev.preventDefault(); const nome = novoQuadro.trim(); if (!nome) return; void agir(() => criarQuadro(cliente, nome)).then((ok) => { if (ok) setNovoQuadro(""); }); }}>
            <label className="flex flex-col gap-0.5">Novo quadro<input className="campo compacto" aria-label="Nome do novo quadro" value={novoQuadro} onChange={(ev) => setNovoQuadro(ev.target.value)} /></label>
            <button type="submit" className="vidro-controle px-2 py-1 font-medium" disabled={novoQuadro.trim() === ""}>Criar quadro</button>
          </form>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Campos deste quadro</h3>
          <p className="text-[color:var(--tinta-fraca)]">O que o card precisa guardar além do padrão — convênio, nº de vidas, data da última consulta. Texto, número, data ou uma lista de opções.</p>
          <ul className="flex flex-col gap-1">
            {props.quadro.campos.map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate">{f.nome} <span className="text-[color:var(--tinta-fraca)]">· {TIPO_DE_CAMPO[f.tipo]}{f.tipo === "opcao" ? ` (${f.opcoes.join(", ")})` : ""}</span></span>
                <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Arquivar o campo ${f.nome}`} onClick={() => void agir(() => arquivarCampo(cliente, f.id))}>Arquivar</button>
              </li>
            ))}
          </ul>
          <form
            className="flex flex-wrap items-end gap-1.5"
            onSubmit={(ev) => {
              ev.preventDefault();
              const nome = novoCampo.nome.trim();
              if (!nome) return;
              const opcoes = novoCampo.opcoes.split(",").map((o) => o.trim()).filter((o) => o !== "");
              void agir(() => criarCampo(cliente, props.quadro.quadroId, { nome, tipo: novoCampo.tipo, ...(novoCampo.tipo === "opcao" ? { opcoes } : {}) })).then((ok) => { if (ok) setNovoCampo({ nome: "", tipo: "texto", opcoes: "" }); });
            }}
          >
            <label className="flex flex-col gap-0.5">Novo campo<input className="campo compacto" aria-label="Nome do novo campo" value={novoCampo.nome} onChange={(ev) => setNovoCampo({ ...novoCampo, nome: ev.target.value })} /></label>
            <label className="flex flex-col gap-0.5">Tipo
              <select className="campo compacto" aria-label="Tipo do novo campo" value={novoCampo.tipo} onChange={(ev) => setNovoCampo({ ...novoCampo, tipo: ev.target.value as CampoDoFunil["tipo"] })}>
                {(Object.keys(TIPO_DE_CAMPO) as CampoDoFunil["tipo"][]).map((t) => <option key={t} value={t}>{TIPO_DE_CAMPO[t]}</option>)}
              </select>
            </label>
            {novoCampo.tipo === "opcao" ? <label className="flex flex-col gap-0.5">Opções (vírgula)<input className="campo compacto" aria-label="Opções do novo campo" value={novoCampo.opcoes} onChange={(ev) => setNovoCampo({ ...novoCampo, opcoes: ev.target.value })} /></label> : null}
            <button type="submit" className="vidro-controle px-2 py-1 font-medium" disabled={novoCampo.nome.trim() === "" || (novoCampo.tipo === "opcao" && novoCampo.opcoes.trim() === "")}>Criar campo</button>
          </form>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Etapas abertas</h3>
          <p className="text-[color:var(--tinta-fraca)]">A probabilidade mora na etapa: é ela que pesa a previsão. Ganho e Perdido são saídas e não se editam.</p>
          <ul className="flex flex-col gap-1.5">
            {abertas.map((e) => <LinhaDeEtapa key={e.id} etapa={e} agir={agir} podeArquivar={abertas.length > 1} />)}
          </ul>
          <form
            className="flex flex-wrap items-end gap-1.5"
            onSubmit={(ev) => {
              ev.preventDefault();
              const nome = novaEtapa.nome.trim();
              const pct = Number(novaEtapa.probabilidadePct);
              if (!nome) return;
              void agir(() => criarEtapa(cliente, { nome, probabilidadePct: Number.isFinite(pct) ? pct : 50, quadroId: props.quadro.quadroId })).then((ok) => { if (ok) setNovaEtapa({ nome: "", probabilidadePct: "50" }); });
            }}
          >
            <label className="flex flex-col gap-0.5">Nova etapa<input className="campo compacto" aria-label="Nome da nova etapa" value={novaEtapa.nome} onChange={(ev) => setNovaEtapa({ ...novaEtapa, nome: ev.target.value })} /></label>
            <label className="flex flex-col gap-0.5">%<input className="campo compacto w-16" inputMode="numeric" aria-label="Probabilidade da nova etapa" value={novaEtapa.probabilidadePct} onChange={(ev) => setNovaEtapa({ ...novaEtapa, probabilidadePct: ev.target.value })} /></label>
            <button type="submit" className="vidro-controle px-2 py-1 font-medium" disabled={novaEtapa.nome.trim() === ""}>Criar etapa</button>
          </form>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Motivos de perda</h3>
          <p className="text-[color:var(--tinta-fraca)]">Perder exige um motivo. É o que o relatório de perdas vai mostrar.</p>
          <ul className="flex flex-col gap-1">
            {props.quadro.motivosDePerda.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2">
                <span>{m.nome}</span>
                <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Arquivar o motivo ${m.nome}`} onClick={() => void agir(() => arquivarMotivoDePerda(cliente, m.id))}>Arquivar</button>
              </li>
            ))}
          </ul>
          <form
            className="flex flex-wrap items-end gap-1.5"
            onSubmit={(ev) => { ev.preventDefault(); const nome = novoMotivo.trim(); if (!nome) return; void agir(() => criarMotivoDePerda(cliente, nome)).then((ok) => { if (ok) setNovoMotivo(""); }); }}
          >
            <label className="flex flex-col gap-0.5">Novo motivo<input className="campo compacto" aria-label="Nome do novo motivo de perda" value={novoMotivo} onChange={(ev) => setNovoMotivo(ev.target.value)} /></label>
            <button type="submit" className="vidro-controle px-2 py-1 font-medium" disabled={novoMotivo.trim() === ""}>Criar motivo</button>
          </form>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Catálogo</h3>
          <p className="text-[color:var(--tinta-fraca)]">Produtos e serviços com preço. Um card composto de itens tem o valor somado deles; o preço fica fotografado no card na hora de acrescentar.</p>
          <ul className="flex flex-col gap-1">
            {(catalogo.data?.itens ?? []).map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate">{i.nome}{i.sku ? <span className="text-[color:var(--tinta-fraca)]"> · {i.sku}</span> : null} <span className="text-[color:var(--tinta-fraca)]">{reais(i.precoCentavos)}</span></span>
                <button type="button" className="text-[color:var(--acao-texto)] underline" aria-label={`Arquivar o item ${i.nome}`} onClick={() => void agir(() => arquivarItemDoCatalogo(cliente, i.id))}>Arquivar</button>
              </li>
            ))}
          </ul>
          <form
            className="flex flex-wrap items-end gap-1.5"
            onSubmit={(ev) => {
              ev.preventDefault();
              const nome = novoItem.nome.trim();
              const n = Number(novoItem.preco.replace(",", "."));
              if (!nome || !Number.isFinite(n)) return;
              void agir(() => criarItemDoCatalogo(cliente, { nome, sku: novoItem.sku.trim() || null, precoCentavos: Math.round(n * 100) })).then((ok) => { if (ok) setNovoItem({ nome: "", sku: "", preco: "" }); });
            }}
          >
            <label className="flex flex-col gap-0.5">Novo item<input className="campo compacto" aria-label="Nome do novo item" value={novoItem.nome} onChange={(ev) => setNovoItem({ ...novoItem, nome: ev.target.value })} /></label>
            <label className="flex flex-col gap-0.5">SKU<input className="campo compacto w-20" aria-label="SKU do novo item" value={novoItem.sku} onChange={(ev) => setNovoItem({ ...novoItem, sku: ev.target.value })} /></label>
            <label className="flex flex-col gap-0.5">R$<input className="campo compacto w-20" inputMode="decimal" aria-label="Preço do novo item" value={novoItem.preco} onChange={(ev) => setNovoItem({ ...novoItem, preco: ev.target.value })} /></label>
            <button type="submit" className="vidro-controle px-2 py-1 font-medium" disabled={novoItem.nome.trim() === "" || novoItem.preco.trim() === ""}>Criar item</button>
          </form>
        </div>
      </div>
      {recado ? <p role="alert" style={{ color: "var(--erro-texto)" }}>{recado}</p> : null}
    </section>
  );
}

function LinhaDeEtapa(props: { readonly etapa: EtapaDoFunil; readonly agir: (fn: () => Promise<{ ok: boolean; falha?: FalhaDaApi }>) => Promise<boolean>; readonly podeArquivar: boolean }) {
  const cliente = useQueryClient();
  const { etapa: e } = props;
  const [nome, setNome] = useState(e.nome);
  const [pct, setPct] = useState(String(e.probabilidadePct));
  const mudou = nome.trim() !== e.nome || Number(pct) !== e.probabilidadePct;
  return (
    <li className="flex flex-wrap items-center gap-1.5">
      <input className="campo compacto min-w-0 flex-1" aria-label={`Nome da etapa ${e.nome}`} value={nome} onChange={(ev) => setNome(ev.target.value)} />
      <input className="campo compacto w-14" inputMode="numeric" aria-label={`Probabilidade da etapa ${e.nome}`} value={pct} onChange={(ev) => setPct(ev.target.value)} />
      <span className="text-[color:var(--tinta-fraca)]">%</span>
      <button
        type="button"
        className="vidro-controle px-2 py-1 font-medium disabled:opacity-100"
        disabled={!mudou}
        aria-label={`Salvar a etapa ${e.nome}`}
        title={mudou ? "Salvar" : "Nada mudou"}
        onClick={() => void props.agir(() => editarEtapa(cliente, e.id, { nome: nome.trim(), probabilidadePct: Number(pct) }))}
      >
        Salvar
      </button>
      {props.podeArquivar ? (
        <button
          type="button"
          className="text-[color:var(--acao-texto)] underline"
          aria-label={`Arquivar a etapa ${e.nome}`}
          onClick={() => { if (window.confirm(`Arquivar a etapa "${e.nome}"? Os cards que estão nela precisam ser movidos antes.`)) void props.agir(() => arquivarEtapa(cliente, e.id)); }}
        >
          Arquivar
        </button>
      ) : null}
    </li>
  );
}

/**
 * Importar oportunidades (spec 10 §6) — colar a planilha, CONFERIR, e só então gravar. A mesma forma
 * da importação de contatos: a conferência mostra as dez primeiras linhas interpretadas e o que foi
 * recusado com o número da linha; etapa desconhecida é dita antes, porque cai na primeira.
 */
function ImportarOportunidades(props: { readonly quadroId: string }) {
  const cliente = useQueryClient();
  const [csv, setCsv] = useState("");
  const [conferido, setConferido] = useState<ConferenciaDeOportunidades | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const conferir = async () => {
    setOcupado(true);
    setErro(null);
    setResultado(null);
    try {
      setConferido(await conferirOportunidades(csv, props.quadroId));
    } catch {
      setErro("Não foi possível conferir a planilha.");
    }
    setOcupado(false);
  };
  const gravar = async () => {
    setOcupado(true);
    setErro(null);
    const r = await importarOportunidades(cliente, csv, props.quadroId);
    setOcupado(false);
    if (!r.ok) {
      setErro(mensagemDa(r.falha) ?? "Não foi possível importar.");
      return;
    }
    setResultado(
      `${r.dados.criadas} oportunidade(s) criada(s), ${r.dados.contatosCriados} contato(s) novo(s)` +
        (r.dados.jaTinham > 0 ? `, ${r.dados.jaTinham} já tinha(m) card aberto` : "") +
        (r.dados.recusadas.length > 0 ? `, ${r.dados.recusadas.length} linha(s) recusada(s)` : "") +
        (r.dados.camposIgnorados > 0 ? `, ${r.dados.camposIgnorados} célula(s) de campo ficaram vazias por não passarem no tipo` : ""),
    );
    setConferido(null);
    setCsv("");
  };
  return (
    <section className="flex flex-col gap-3 text-xs" aria-label="Importar oportunidades">
      <p className="text-[color:var(--tinta-fraca)]">
        Uma linha por oportunidade: <strong>telefone com código do país</strong>; nome; título; valor; etapa; temperatura; previsão (AAAA-MM-DD); observação. Só o telefone é obrigatório. O contato nasce se não existir; quem já tem card aberto não ganha um segundo. Com uma linha de cabeçalho, as colunas seguintes cujo título é o nome de um campo deste quadro entram nesse campo.
      </p>
      <textarea
        className="campo compacto min-h-32 w-full font-mono"
        aria-label="Planilha de oportunidades"
        value={csv}
        onChange={(e) => { setCsv(e.target.value); setConferido(null); }}
        placeholder={"5527999998888;Maria Silva;Plano anual;1.200,00;Proposta;quente;2026-10-05;veio da outra ferramenta"}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Botao tipo="secundario" onClick={() => void conferir()} desabilitado={ocupado || csv.trim() === ""} motivoDeDesabilitar="Cole a planilha primeiro.">Conferir</Botao>
        <Botao onClick={() => void gravar()} desabilitado={ocupado || conferido === null || conferido.quantasAceitas === 0} motivoDeDesabilitar={conferido === null ? "Confira antes de importar." : "Nenhuma linha da planilha serve."}>
          Importar {conferido ? `${conferido.quantasAceitas} oportunidade(s)` : ""}
        </Botao>
      </div>
      {erro ? <p role="alert" style={{ color: "var(--erro-texto)" }}>{erro}</p> : null}
      {resultado ? <p className="text-[color:var(--tinta-fraca)]">{resultado}</p> : null}
      {conferido ? (
        <div className="flex flex-col gap-2">
          {conferido.etapasDesconhecidas.length > 0 ? (
            <p style={{ color: "var(--atencao-texto)" }}>Etapas que não existem no funil e vão cair na primeira: {conferido.etapasDesconhecidas.join(", ")}. Crie-as em Etapas e motivos antes, se quiser mantê-las.</p>
          ) : null}
          {conferido.colunasExtras.length > 0 ? (
            <ul className="flex flex-col gap-0.5" aria-label="Colunas extras">
              {conferido.colunasExtras.map((c, i) => (
                <li key={`${c.titulo}-${i}`}>
                  Coluna {9 + i} "{c.titulo}": {c.campoNome ? <>entra no campo <strong>{c.campoNome}</strong> ({TIPO_DE_CAMPO[c.tipo ?? "texto"]})</> : <span style={{ color: "var(--atencao-texto)" }}>não há campo com esse nome neste quadro — será ignorada. Crie o campo em Etapas e motivos, se quiser guardá-la.</span>}
                </li>
              ))}
              {conferido.camposIlegiveis > 0 ? <li style={{ color: "var(--atencao-texto)" }}>{conferido.camposIlegiveis} célula(s) não passam no tipo do campo e vão ficar vazias — o card entra mesmo assim.</li> : null}
            </ul>
          ) : null}
          <p className="font-medium">Como as colunas foram lidas ({conferido.quantasAceitas} linha(s) servem)</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left text-[color:var(--tinta-fraca)]"><tr><th className="pr-2">Linha</th><th className="pr-2">Telefone</th><th className="pr-2">Nome</th><th className="pr-2">Título</th><th className="pr-2">Valor</th><th className="pr-2">Etapa</th><th className="pr-2">Temp.</th><th>Previsão</th></tr></thead>
              <tbody>
                {conferido.amostra.map((l) => (
                  <tr key={l.linha} className="border-t border-[color:var(--borda)]">
                    <td className="pr-2">{l.linha}</td><td className="pr-2 font-mono">{l.identificador}</td><td className="pr-2">{l.nome ?? "—"}</td><td className="pr-2">{l.titulo ?? "—"}</td><td className="pr-2">{l.valorCentavos === null ? "—" : reais(l.valorCentavos)}</td><td className="pr-2">{l.etapa ?? "(primeira)"}</td><td className="pr-2">{l.temperatura ?? "—"}</td><td>{l.previsaoDeFechamento ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {conferido.recusadas.length > 0 ? (
            <ul className="flex flex-col gap-0.5" aria-label="Linhas recusadas">
              {conferido.recusadas.map((r) => <li key={r.linha}>Linha {r.linha}: {r.explicacao}</li>)}
              {conferido.quantasRecusadas > conferido.recusadas.length ? <li>… e mais {conferido.quantasRecusadas - conferido.recusadas.length}.</li> : null}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ReabrirBotao(props: { readonly id: string }) {
  const cliente = useQueryClient();
  const [recado, setRecado] = useState<string | null>(null);
  return (
    <>
      <button type="button" className="v2-link" onClick={() => void reabrirOportunidade(cliente, props.id).then((r) => { if (!r.ok) setRecado(mensagemDa(r.falha) ?? "Não deu."); })}>
        Reabrir
      </button>
      {recado ? <span role="alert" style={{ color: "var(--erro-texto)" }}>{recado}</span> : null}
    </>
  );
}

function NovaOportunidade(props: { readonly quadro: QuadroDoFunil; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [busca, setBusca] = useState("");
  const [contatoId, setContatoId] = useState<string | null>(null);
  const [titulo, setTitulo] = useState("");
  const [valor, setValor] = useState("");
  const [recado, setRecado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const contatos = useContatos(busca, null);

  const salvar = async () => {
    if (!contatoId) return;
    setOcupado(true);
    setRecado(null);
    const n = Number(valor.replace(",", "."));
    const r = await criarOportunidade(cliente, {
      contatoId,
      quadroId: props.quadro.quadroId,
      titulo: titulo.trim() || undefined,
      valorCentavos: valor.trim() === "" ? undefined : Number.isFinite(n) ? Math.round(n * 100) : undefined,
    });
    setOcupado(false);
    if (r.ok) props.aoFechar();
    else setRecado(mensagemDa(r.falha) ?? "Não deu certo. Tente de novo.");
  };

  return (
    <section className="flex flex-col gap-2" aria-label="Nova oportunidade">
      <label className="flex flex-col gap-1 text-xs">
        Contato
        <input className="campo compacto" placeholder="Buscar por nome ou número" value={busca} onChange={(e) => { setBusca(e.target.value); setContatoId(null); }} />
      </label>
      {busca.trim() && contatos.data ? (
        <ul className="max-h-40 overflow-auto text-xs">
          {contatos.data.itens.slice(0, 20).map((c) => (
            <li key={c.id}>
              <button type="button" className={`w-full text-left ${contatoId === c.id ? "font-medium" : ""}`} aria-pressed={contatoId === c.id} onClick={() => setContatoId(c.id)}>
                {c.nome ?? c.nomeDoPerfil ?? "—"} <span className="font-mono text-[color:var(--tinta-fraca)]">{c.identificador}</span>
              </button>
            </li>
          ))}
          {contatos.data.itens.length === 0 ? <li className="text-[color:var(--tinta-fraca)]">Nenhum contato com esse nome.</li> : null}
        </ul>
      ) : null}
      <label className="flex flex-col gap-1 text-xs">
        Título (opcional)
        <input className="campo compacto" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      </label>
      {props.quadro.verValores ? (
        <label className="flex flex-col gap-1 text-xs">
          Valor estimado R$ (opcional)
          <input className="campo compacto w-32" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />
        </label>
      ) : null}
      {recado ? <p className="text-xs" role="alert" style={{ color: "var(--erro-texto)" }}>{recado}</p> : null}
      <div className="flex gap-2">
        <Botao desabilitado={!contatoId || ocupado} motivoDeDesabilitar="Escolha o contato." onClick={() => void salvar()}>Abrir no funil</Botao>
        <Botao tipo="secundario" onClick={props.aoFechar}>Fechar</Botao>
      </div>
    </section>
  );
}
